import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/components/AuthProvider';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Copy, Ruler, Info, Share2, MessageCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TabelaMedidaPadrao {
  id: string;
  empresa_id: string;
  tipo_peca: string;
  tamanho: string;
  peito?: number;
  cintura?: number;
  quadril?: number;
  comprimento?: number;
  ombro?: number;
  manga?: number;
  descricao?: string;
  created_at: string;
  updated_at: string;
}

export default function TabelaMedidas() {
  const { empresa } = useAuth();
  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTabela, setEditingTabela] = useState<TabelaMedidaPadrao | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTipoPeca, setSelectedTipoPeca] = useState<string>("all");
  const [ordenacao, setOrdenacao] = useState<"nome-az" | "nome-za" | "recentes" | "antigos">("recentes");

  const [formData, setFormData] = useState({
    tipo_peca: "camiseta",
    tamanho: "P",
    peito: "",
    cintura: "",
    quadril: "",
    comprimento: "",
    ombro: "",
    manga: "",
    descricao: "",
  });

  // Buscar tabelas de medidas
  const { data: tabelas = [], isLoading, refetch } = useQuery({
    queryKey: ['tabelaMedidas', empresa?.id],
    queryFn: async () => {
      if (!empresa?.id) return [];
      const { data, error } = await supabase
        .from('tabela_medidas_padrao')
        .select('*')
        .eq('empresa_id', empresa.id)
        .order('tipo_peca, tamanho', { ascending: true });

      if (error) {
        toast.error('Erro ao carregar tabelas de medidas');
        return [];
      }
      return (data as TabelaMedidaPadrao[]) || [];
    },
    enabled: !!empresa?.id,
  });

  // Tipos de peças disponíveis
  const tiposPeca = ['camiseta', 'blusa', 'vestido', 'calça', 'saia', 'jaqueta', 'calção', 'outros'];
  const tamanhos = ['P', 'M', 'G', 'GG', 'PP', 'XP', 'XG', 'XGG'];

  // Filtrar e ordenar tabelas
  const filteredTabelas = tabelas
    .filter(tabela => {
      const matchesSearch =
        tabela.tipo_peca.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tabela.tamanho.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTipo = selectedTipoPeca === "all" || tabela.tipo_peca === selectedTipoPeca;
      return matchesSearch && matchesTipo;
    })
    .sort((a, b) => {
      switch (ordenacao) {
        case "nome-az":
          return `${a.tipo_peca}-${a.tamanho}`.localeCompare(`${b.tipo_peca}-${b.tamanho}`);
        case "nome-za":
          return `${b.tipo_peca}-${b.tamanho}`.localeCompare(`${a.tipo_peca}-${a.tamanho}`);
        case "recentes":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "antigos":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        default:
          return 0;
      }
    });

  // Agrupar por tipo de peça
  const tabelasAgrupadas = filteredTabelas.reduce((acc, tabela) => {
    if (!acc[tabela.tipo_peca]) {
      acc[tabela.tipo_peca] = [];
    }
    acc[tabela.tipo_peca].push(tabela);
    return acc;
  }, {} as Record<string, TabelaMedidaPadrao[]>);

  const resetForm = () => {
    setFormData({
      tipo_peca: "camiseta",
      tamanho: "P",
      peito: "",
      cintura: "",
      quadril: "",
      comprimento: "",
      ombro: "",
      manga: "",
      descricao: "",
    });
    setEditingTabela(null);
  };

  const handleOpenDialog = (tabela?: TabelaMedidaPadrao) => {
    if (tabela) {
      setEditingTabela(tabela);
      setFormData({
        tipo_peca: tabela.tipo_peca,
        tamanho: tabela.tamanho,
        peito: tabela.peito?.toString() || "",
        cintura: tabela.cintura?.toString() || "",
        quadril: tabela.quadril?.toString() || "",
        comprimento: tabela.comprimento?.toString() || "",
        ombro: tabela.ombro?.toString() || "",
        manga: tabela.manga?.toString() || "",
        descricao: tabela.descricao || "",
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!empresa?.id || !formData.tipo_peca || !formData.tamanho) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }

    try {
      const payload = {
        empresa_id: empresa.id,
        tipo_peca: formData.tipo_peca,
        tamanho: formData.tamanho,
        peito: formData.peito ? Number(formData.peito) : null,
        cintura: formData.cintura ? Number(formData.cintura) : null,
        quadril: formData.quadril ? Number(formData.quadril) : null,
        comprimento: formData.comprimento ? Number(formData.comprimento) : null,
        ombro: formData.ombro ? Number(formData.ombro) : null,
        manga: formData.manga ? Number(formData.manga) : null,
        descricao: formData.descricao || null,
      };

      if (editingTabela) {
        const { error } = await supabase
          .from('tabela_medidas_padrao')
          .update(payload)
          .eq('id', editingTabela.id);

        if (error) throw error;
        toast.success("Tabela atualizada com sucesso!");
      } else {
        const { error } = await supabase
          .from('tabela_medidas_padrao')
          .insert([payload]);

        if (error) throw error;
        toast.success("Tabela criada com sucesso!");
      }

      setIsDialogOpen(false);
      resetForm();
      refetch();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar tabela de medidas");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja deletar esta tabela de medidas?")) return;

    try {
      const { error } = await supabase
        .from('tabela_medidas_padrao')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success("Tabela deletada com sucesso!");
      refetch();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao deletar tabela");
    }
  };

  const handleDuplicate = async (tabela: TabelaMedidaPadrao) => {
    setEditingTabela(null);
    setFormData({
      tipo_peca: tabela.tipo_peca,
      tamanho: "",
      peito: tabela.peito?.toString() || "",
      cintura: tabela.cintura?.toString() || "",
      quadril: tabela.quadril?.toString() || "",
      comprimento: tabela.comprimento?.toString() || "",
      ombro: tabela.ombro?.toString() || "",
      manga: tabela.manga?.toString() || "",
      descricao: tabela.descricao || "",
    });
    setIsDialogOpen(true);
  };

  const formatarMedidas = (tabela: TabelaMedidaPadrao): string => {
    const linhas = [
      `📏 *TABELA DE MEDIDAS - ${tabela.tipo_peca.toUpperCase()}*`,
      `Tamanho: *${tabela.tamanho}*`,
      "",
    ];

    if (tabela.peito) linhas.push(`👕 Peito: ${tabela.peito}cm`);
    if (tabela.cintura) linhas.push(`🎀 Cintura: ${tabela.cintura}cm`);
    if (tabela.quadril) linhas.push(`👖 Quadril: ${tabela.quadril}cm`);
    if (tabela.comprimento) linhas.push(`📐 Comprimento: ${tabela.comprimento}cm`);
    if (tabela.ombro) linhas.push(`💪 Ombro: ${tabela.ombro}cm`);
    if (tabela.manga) linhas.push(`🎽 Manga: ${tabela.manga}cm`);

    if (tabela.descricao) {
      linhas.push("");
      linhas.push(`📝 Observação: ${tabela.descricao}`);
    }

    return linhas.join("\n");
  };

  const handleCopyMedidas = (tabela: TabelaMedidaPadrao) => {
    const texto = formatarMedidas(tabela);
    navigator.clipboard.writeText(texto);
    toast.success("Medidas copiadas para área de transferência! 📋");
  };

  const handleShareWhatsApp = (tabela: TabelaMedidaPadrao) => {
    const texto = formatarMedidas(tabela);
    const mensagemCodificada = encodeURIComponent(texto);
    const link = `https://wa.me/?text=${mensagemCodificada}`;
    window.open(link, "_blank");
  };

  return (
    <div className="flex flex-col gap-6 min-h-screen bg-gray-50">
      <div className="sticky top-0 z-40 border-b bg-white">
        <div className="flex items-center gap-4 p-4">
          <SidebarTrigger />
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Ruler className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tabela de Medidas Padrão</h1>
              <p className="text-sm text-gray-600">Crie e gerencie tabelas de medidas padrão para suas peças</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 py-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Card de boas-vindas */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-purple-600 mt-1" />
                  <div>
                    <CardTitle className="text-lg">Como usar tabelas de medidas</CardTitle>
                    <CardDescription className="mt-2">
                      Crie tabelas padrão para cada tipo de peça (P, M, G, etc.). Essas medidas serão usadas como referência
                      quando você criar pedidos e orçamentos, ajudando seus clientes a escolher o tamanho correto.
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Controles */}
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            <div className="flex-1">
              <Input
                placeholder="Buscar por tipo de peça ou tamanho..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <select
              value={selectedTipoPeca}
              onChange={(e) => setSelectedTipoPeca(e.target.value)}
              className="px-3 py-2 border rounded-lg"
              title="Filtrar por tipo de peça"
            >
              <option value="all">Todos os tipos</option>
              {Array.from(new Set(tabelas.map(t => t.tipo_peca))).sort().map(tipo => (
                <option key={tipo} value={tipo}>
                  {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                </option>
              ))}
            </select>
            <select
              value={ordenacao}
              onChange={(e) => setOrdenacao(e.target.value as "nome-az" | "nome-za" | "recentes" | "antigos")}
              className="px-3 py-2 border rounded-lg"
              title="Ordenar"
            >
              <option value="recentes">Mais recentes</option>
              <option value="antigos">Mais antigos</option>
              <option value="nome-az">A-Z</option>
              <option value="nome-za">Z-A</option>
            </select>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Nova Tabela
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingTabela ? "Editar Tabela de Medidas" : "Criar Tabela de Medidas"}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Peça *</Label>
                      <Input
                        list="tiposPecaList"
                        value={formData.tipo_peca}
                        onChange={(e) => setFormData({...formData, tipo_peca: e.target.value.toLowerCase()})}
                        placeholder="Ex: Camiseta, Blusa, Personalizado..."
                        required
                      />
                      <datalist id="tiposPecaList">
                        {tiposPeca.map(tipo => (
                          <option key={tipo} value={tipo.charAt(0).toUpperCase() + tipo.slice(1)} />
                        ))}
                      </datalist>
                      <p className="text-xs text-muted-foreground">
                        💡 Escolha de uma lista ou digite um nome customizado
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Tamanho *</Label>
                      <Input
                        list="tamanhosList"
                        value={formData.tamanho}
                        onChange={(e) => setFormData({...formData, tamanho: e.target.value.toUpperCase()})}
                        placeholder="Ex: P, M, G, XGG, Único..."
                        required
                      />
                      <datalist id="tamanhosList">
                        {tamanhos.map(tamanho => (
                          <option key={tamanho} value={tamanho} />
                        ))}
                      </datalist>
                      <p className="text-xs text-muted-foreground">
                        💡 Escolha de uma lista ou digite um tamanho customizado
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Peito (cm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.peito}
                        onChange={(e) => setFormData({...formData, peito: e.target.value})}
                        placeholder="Ex: 38"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cintura (cm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.cintura}
                        onChange={(e) => setFormData({...formData, cintura: e.target.value})}
                        placeholder="Ex: 35"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Quadril (cm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.quadril}
                        onChange={(e) => setFormData({...formData, quadril: e.target.value})}
                        placeholder="Ex: 48"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Comprimento (cm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.comprimento}
                        onChange={(e) => setFormData({...formData, comprimento: e.target.value})}
                        placeholder="Ex: 70"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ombro (cm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.ombro}
                        onChange={(e) => setFormData({...formData, ombro: e.target.value})}
                        placeholder="Ex: 38"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Manga (cm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.manga}
                        onChange={(e) => setFormData({...formData, manga: e.target.value})}
                        placeholder="Ex: 58"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Descrição ou Observações</Label>
                    <Input
                      value={formData.descricao}
                      onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                      placeholder="Ex: Modelo ajustado, comprimento regular..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleSave} className="flex-1">
                      {editingTabela ? "Atualizar" : "Criar"}
                    </Button>
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Tabelas agrupadas */}
          {isLoading ? (
            <Card>
              <CardContent className="pt-8">
                <div className="text-center text-gray-500">Carregando...</div>
              </CardContent>
            </Card>
          ) : Object.keys(tabelasAgrupadas).length === 0 ? (
            <Card>
              <CardContent className="pt-8">
                <div className="text-center space-y-3">
                  <Ruler className="w-12 h-12 text-gray-300 mx-auto" />
                  <p className="text-gray-600">Nenhuma tabela de medidas criada ainda</p>
                  <p className="text-sm text-gray-500">Clique em "Nova Tabela" para começar</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            Object.entries(tabelasAgrupadas).map(([tipoPeca, tabelasDoTipo]) => (
              <Card key={tipoPeca} className="overflow-hidden">
                <CardHeader className="bg-gray-50 border-b">
                  <CardTitle className="text-lg capitalize">
                    {tipoPeca}
                  </CardTitle>
                  <CardDescription>
                    {tabelasDoTipo.length} tamanho(s) configurado(s)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">Tamanho</TableHead>
                          <TableHead className="text-right">Peito</TableHead>
                          <TableHead className="text-right">Cintura</TableHead>
                          <TableHead className="text-right">Quadril</TableHead>
                          <TableHead className="text-right">Comprimento</TableHead>
                          <TableHead className="text-right">Ombro</TableHead>
                          <TableHead className="text-right">Manga</TableHead>
                          <TableHead>Observações</TableHead>
                          <TableHead className="text-right w-20">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tabelasDoTipo.map((tabela) => (
                          <TableRow key={tabela.id} className="hover:bg-gray-50">
                            <TableCell className="font-semibold">
                              <Badge variant="secondary">{tabela.tamanho}</Badge>
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {tabela.peito ? `${tabela.peito}cm` : "-"}
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {tabela.cintura ? `${tabela.cintura}cm` : "-"}
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {tabela.quadril ? `${tabela.quadril}cm` : "-"}
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {tabela.comprimento ? `${tabela.comprimento}cm` : "-"}
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {tabela.ombro ? `${tabela.ombro}cm` : "-"}
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {tabela.manga ? `${tabela.manga}cm` : "-"}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                              {tabela.descricao || "-"}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 justify-end">
                                <button
                                  onClick={() => handleCopyMedidas(tabela)}
                                  className="p-1.5 hover:bg-blue-50 rounded"
                                  title="Copiar medidas"
                                >
                                  <Copy className="w-4 h-4 text-blue-600" />
                                </button>
                                <button
                                  onClick={() => handleShareWhatsApp(tabela)}
                                  className="p-1.5 hover:bg-green-50 rounded"
                                  title="Compartilhar via WhatsApp"
                                >
                                  <MessageCircle className="w-4 h-4 text-green-600" />
                                </button>
                                <button
                                  onClick={() => handleDuplicate(tabela)}
                                  className="p-1.5 hover:bg-gray-100 rounded"
                                  title="Duplicar"
                                >
                                  <Share2 className="w-4 h-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={() => handleOpenDialog(tabela)}
                                  className="p-1.5 hover:bg-gray-100 rounded"
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={() => handleDelete(tabela.id)}
                                  className="p-1.5 hover:bg-red-50 rounded"
                                  title="Deletar"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
